import React, { FC, useEffect, useMemo } from 'react';

import {
  FormControl,
  FormHelperText,
  FormErrorMessage,
  Checkbox,
  Stack,
} from '@chakra-ui/react';
import { forEach } from 'lodash';
import { useFormContext } from 'react-hook-form';

import { useErrorMessage } from '../index';
import { useStyles } from '../index';
import { FieldProps, CheckboxFieldSchema, CheckboxFieldStyles } from '../types';

import { FormLabel } from './FormLabel';

export const CheckboxField: FC<FieldProps<CheckboxFieldSchema>> = ({
  id,
  name,
  field,
}) => {
  const {
    label,
    helperText,
    isRequired,
    shouldDisplay,
    isDisabled,
    helpIcon,
    styles = {},
    helpTooltip,
    onClickHelpIcon,
  } = field;

  const { register, watch, setValue, getValues } = useFormContext();

  const values = watch();

  const fieldStyles = useStyles<CheckboxFieldStyles>('checkboxField', styles);

  const errorMessage = useErrorMessage(name, label);

  // Register without handing react-hook-form the checkbox refs: it would
  // push the object value onto every box and tick unchecked ones. The value
  // is kept with setValue below instead.
  useEffect(() => {
    register(name);
  }, [register, name]);

  useEffect(() => {
    let data: Record<string, any> = {};
    forEach(
      field?.checkboxes,
      o => (data[o?.name] = o?.defaultChecked ?? o?.isChecked),
    );
    setValue(name, data);
  }, [field?.checkboxes, name, setValue]);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  return isVisible ? (
    <FormControl
      key={`${name}-control`}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
      mb={2}
      {...fieldStyles.control}
    >
      {!!label && (
        <FormLabel
          htmlFor={name}
          label={label}
          helpIcon={helpIcon}
          styles={fieldStyles.label}
          helpTooltip={helpTooltip}
          isRequired={isRequired}
          onClickHelpIcon={onClickHelpIcon}
        />
      )}
      <Stack {...fieldStyles.checkboxGroup}>
        {field.checkboxes.map(({ onChange, ...checkbox }) => (
          <Checkbox
            key={checkbox.name}
            //name={checkbox.name}
            isDisabled={isDisabled}
            data-testid={`${id}-${checkbox.name}`}
            {...checkbox}
            // Applied after the spread so a checkbox's own onChange no longer
            // replaces this one; store `checked` and keep the other boxes.
            onChange={e => {
              onChange?.(e);
              setValue(name, {
                ...getValues(name),
                [checkbox.name]: e.target.checked,
              });
            }}
          >
            {checkbox.label || checkbox.name}
          </Checkbox>
        ))}
      </Stack>
      {!!helperText && (
        <FormHelperText {...fieldStyles.helperText}>
          {helperText}
        </FormHelperText>
      )}
      <FormErrorMessage {...fieldStyles.errorMessage}>
        {errorMessage}
      </FormErrorMessage>
    </FormControl>
  ) : null;
};
